import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import type { AllocationPeriod, AllocationStatus, BillableStatus } from '../types/delivery';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Where the PMO works, so "today" means the same day for everyone who asks. */
export const PMO_TIME_ZONE = 'Asia/Kolkata';

const DATE_ONLY_LENGTH = 10;

/** Statuses a row was closed with by hand before review 5; such a row is past whatever its dates say. */
const CLOSED_STATUSES: readonly AllocationStatus[] = ['completed', 'cancelled'];

export const CLOSED_ALLOCATION_STATUSES = CLOSED_STATUSES;

/** The date in India as `YYYY-MM-DD`. Pass a moment only to make a test deterministic. */
export const todayInIndia = (now: Date = new Date()): string =>
  dayjs(now).tz(PMO_TIME_ZONE).format('YYYY-MM-DD');

/** The day before an India date, as `YYYY-MM-DD`. */
export const dayBefore = (date: string): string =>
  dayjs.utc(date.slice(0, DATE_ONLY_LENGTH)).subtract(1, 'day').format('YYYY-MM-DD');

/** The day after an India date, as `YYYY-MM-DD`. */
export const dayAfter = (date: string): string =>
  dayjs.utc(date.slice(0, DATE_ONLY_LENGTH)).add(1, 'day').format('YYYY-MM-DD');

/** An India date `days` later, as `YYYY-MM-DD`. */
export const daysAfter = (date: string, days: number): string =>
  dayjs.utc(date.slice(0, DATE_ONLY_LENGTH)).add(days, 'day').format('YYYY-MM-DD');

export const toDateOnly = (value: string | Date): string =>
  (typeof value === 'string' ? value : value.toISOString()).slice(0, DATE_ONLY_LENGTH);

export interface AllocationPeriodInput {
  status: AllocationStatus;
  startDate: string | Date;
  /** Null is an old open-ended row, which is never past by date. */
  endDate: string | Date | null;
}

/**
 * Whether an allocation is live. The one place that decides it: the dashboard,
 * over-allocation checks, utilisation, the allocation tabs and the people
 * views all ask here, so a row closed by hand can never read as current in
 * one of them and past in another. The end date is inclusive.
 */
export const allocationPeriod = (
  allocation: AllocationPeriodInput,
  today: string,
): AllocationPeriod => {
  if (CLOSED_STATUSES.includes(allocation.status)) return 'past';

  if (toDateOnly(allocation.startDate) > today) return 'future';

  if (allocation.endDate !== null && toDateOnly(allocation.endDate) < today) return 'past';

  return 'current';
};

/**
 * Only two billabilities are written, but old rows hold `internal` and
 * `overhead`. Every total, filter and label reads them through this, so an
 * old internal row counts as non-billable instead of vanishing from the sums.
 */
export const effectiveBillability = (status: BillableStatus): 'billable' | 'non_billable' =>
  status === 'billable' ? 'billable' : 'non_billable';
